"use client";

import { Copy, FileText, Mail } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const EMAIL = "hello@davidpaulromero.com";
const RESUME_URL = "/assets/DavidPaulRomero_Resume_Aug17.pdf";

function LinkedInMark() {
  return (
    <svg aria-hidden="true" fill="currentColor" viewBox="0 0 24 24">
      <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.36V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.37-1.85 3.6 0 4.26 2.37 4.26 5.46v6.28ZM5.34 7.43A2.06 2.06 0 1 1 5.33 3.3a2.06 2.06 0 0 1 .01 4.13ZM7.12 20.45H3.56V9h3.56v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0Z" />
    </svg>
  );
}

function TwitterMark() {
  return (
    <svg aria-hidden="true" fill="currentColor" viewBox="0 0 512 512">
      <path d="M459.37 151.72c.33 4.55.33 9.1.33 13.65 0 138.72-105.59 298.56-298.56 298.56-59.45 0-114.68-17.22-161.14-47.11 8.45.97 16.57 1.3 25.34 1.3 49.06 0 94.21-16.57 130.27-44.83-46.13-.98-84.79-31.19-98.11-72.77 6.5.97 13 1.62 19.82 1.62 9.42 0 18.84-1.3 27.61-3.57-48.08-9.75-84.14-51.98-84.14-102.99v-1.3c13.97 7.8 30.21 12.67 47.43 13.32-28.26-18.84-46.78-51-46.78-87.39 0-19.49 5.2-37.36 14.29-52.95 51.66 63.67 129.3 105.26 216.37 109.81-1.62-7.8-2.6-15.92-2.6-24.04 0-57.83 46.78-104.93 104.93-104.93 30.21 0 57.5 12.67 76.67 33.14 23.71-4.55 46.46-13.32 66.6-25.34-7.8 24.37-24.37 44.83-46.13 57.83 21.12-2.27 41.58-8.12 60.43-16.24-14.29 20.79-32.16 39.31-52.63 54.25Z" />
    </svg>
  );
}

function GitHubMark() {
  return (
    <svg aria-hidden="true" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 .3a12 12 0 0 0-3.79 23.39c.6.11.82-.26.82-.58v-2.26c-3.34.73-4.04-1.42-4.04-1.42-.55-1.39-1.33-1.76-1.33-1.76-1.09-.74.08-.73.08-.73 1.2.08 1.84 1.23 1.84 1.23 1.07 1.83 2.81 1.3 3.49 1 .11-.77.42-1.3.76-1.6-2.66-.3-5.47-1.33-5.47-5.93 0-1.31.47-2.38 1.23-3.22-.12-.3-.53-1.53.12-3.19 0 0 1.01-.32 3.3 1.23a11.48 11.48 0 0 1 6 0c2.29-1.55 3.3-1.23 3.3-1.23.65 1.66.24 2.89.12 3.19.77.84 1.23 1.91 1.23 3.22 0 4.61-2.81 5.63-5.48 5.93.43.37.81 1.1.81 2.21v3.28c0 .32.22.69.83.58A12 12 0 0 0 12 .3Z" />
    </svg>
  );
}

const socialLinks = [
  { label: "LinkedIn", href: "https://www.linkedin.com/in/vibrantnebula", icon: <LinkedInMark /> },
  { label: "Resume", href: RESUME_URL, icon: <FileText aria-hidden="true" />, external: false },
  { label: "Twitter", href: "https://www.twitter.com/vibrantnebula", icon: <TwitterMark /> },
  { label: "GitHub", href: "https://www.github.com/vibrantnebula", icon: <GitHubMark /> },
];

async function copyText(text: string) {
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Safari can expose this API but reject writes from some embedded views.
    }
  }

  const field = document.createElement("textarea");
  field.value = text;
  field.setAttribute("readonly", "");
  field.style.fontSize = "16px";
  field.style.left = "-9999px";
  field.style.opacity = "0";
  field.style.position = "fixed";
  field.style.top = "0";
  document.body.appendChild(field);
  field.select();
  field.setSelectionRange(0, field.value.length);

  try {
    return typeof document.execCommand === "function" && document.execCommand("copy");
  } catch {
    return false;
  } finally {
    document.body.removeChild(field);
  }
}

export function SocialCard() {
  const [copyStatus, setCopyStatus] = useState("Copy email");
  const resetTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => {
    if (resetTimer.current !== undefined) window.clearTimeout(resetTimer.current);
  }, []);

  const copyEmail = async () => {
    setCopyStatus("Copying...");
    const copied = await copyText(EMAIL).catch(() => false);
    setCopyStatus(copied ? "Copied!" : "Copy failed");

    if (resetTimer.current !== undefined) window.clearTimeout(resetTimer.current);
    resetTimer.current = window.setTimeout(() => setCopyStatus("Copy email"), 1800);
  };

  return (
    <div className="social-card" aria-label="Contact and social links">
      <div className="social-card-email">
        <a className="social-card-email-link" href={`mailto:${EMAIL}`}>
          <Mail aria-hidden="true" size={22} strokeWidth={1.8} />
          <span>{EMAIL}</span>
        </a>
        <button className="social-card-copy" onClick={copyEmail} type="button">
          <Copy aria-hidden="true" size={14} strokeWidth={1.8} />
          <span aria-live="polite">{copyStatus}</span>
        </button>
      </div>
      <div className="social-card-links">
        {socialLinks.map(({ label, href, icon, external = true }) => (
          <a
            aria-label={external ? `David Paul Romero on ${label}` : "Open David Paul Romero resume"}
            href={href}
            key={label}
            rel={external ? "noopener noreferrer" : undefined}
            target={external ? "_blank" : undefined}
          >
            {icon}
            <span>{label}</span>
          </a>
        ))}
      </div>
    </div>
  );
}
